import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import {
  Phone as PhoneIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Download as DownloadIcon,
  DeleteOutline as DeleteOutlineIcon,
  VpnKey as VpnKeyIcon,
  QrCode2 as QrCodeIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "calc(100vh - 64px)",
    overflowY: "auto",
  },
  header: {
    marginBottom: "24px",
  },
  title: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  subtitle: {
    fontSize: "14px",
    color: "#666",
    marginTop: "4px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "24px",
    "@media (max-width: 900px)": {
      gridTemplateColumns: "1fr",
    },
  },
  panel: {
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    overflow: "hidden",
  },
  panelHeader: {
    padding: "16px 20px",
    borderBottom: "1px solid #f0f0f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "& h3": {
      margin: 0,
      fontSize: "16px",
      fontWeight: 700,
      color: "#1a1a1a",
    },
  },
  panelBody: {
    padding: "20px",
  },
  formGroup: {
    marginBottom: "16px",
  },
  label: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#374151",
    marginBottom: "6px",
    display: "block",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #e8ecf1",
    borderRadius: "8px",
    fontSize: "14px",
    transition: "all 0.2s",
    boxSizing: "border-box",
    "&:focus": {
      outline: "none",
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
  },
  btn: {
    padding: "10px 18px",
    borderRadius: "8px",
    border: "none",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.2s",
  },
  btnPrimary: {
    background: "#16a34a",
    color: "white",
    "&:hover": { background: "#15803d" },
    "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
  },
  btnDanger: {
    background: "#ef4444",
    color: "white",
    "&:hover": { background: "#dc2626" },
  },
  btnOutline: {
    background: "white",
    color: "#374151",
    border: "1px solid #e8ecf1",
    "&:hover": { background: "#f9fafb" },
  },
  sessionItem: {
    padding: "12px 16px",
    borderBottom: "1px solid #f5f5f5",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "&:last-child": { borderBottom: "none" },
  },
  sessionInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  sessionName: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#1a1a1a",
  },
  sessionStatus: {
    fontSize: "12px",
    color: "#666",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    display: "inline-block",
  },
  statusCard: {
    padding: "16px",
    borderRadius: "8px",
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    marginBottom: "16px",
    fontSize: "13px",
    color: "#166534",
  },
  qrImg: {
    display: "block",
    margin: "16px auto",
    border: "2px solid #e8ecf1",
    borderRadius: "8px",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  modalContent: {
    background: "white",
    borderRadius: "16px",
    width: "90%",
    maxWidth: "520px",
    maxHeight: "85vh",
    overflow: "auto",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  },
  modalHeader: {
    padding: "20px 24px",
    borderBottom: "1px solid #f0f0f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "& h3": {
      margin: 0,
      fontSize: "18px",
      fontWeight: 700,
      color: "#1a1a1a",
    },
  },
  modalBody: {
    padding: "24px",
  },
  modalCloseBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px",
    borderRadius: "6px",
    color: "#666",
    "&:hover": { background: "#f5f5f5" },
  },
  optionCard: {
    padding: "20px",
    border: "2px solid #e8ecf1",
    borderRadius: "12px",
    cursor: "pointer",
    transition: "all 0.2s",
    marginBottom: "12px",
    "&:hover": {
      borderColor: "#3b82f6",
      background: "#f0f7ff",
    },
  },
  optionCardActive: {
    borderColor: "#16a34a",
    background: "#f0fdf4",
  },
  optionTitle: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#1a1a1a",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "4px",
  },
  optionDesc: {
    fontSize: "12px",
    color: "#666",
    margin: 0,
    paddingLeft: "28px",
  },
  textarea: {
    width: "100%",
    minHeight: "180px",
    padding: "12px",
    border: "1px solid #e8ecf1",
    borderRadius: "8px",
    fontSize: "12px",
    fontFamily: "monospace",
    resize: "vertical",
    boxSizing: "border-box",
    "&:focus": {
      outline: "none",
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
  },
}));

const WaCalls = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  // Config form
  const [wacallsUrl, setWacallsUrl] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // State
  const [token, setToken] = useState("");
  const [loggedIn, setLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState("");
  const [qrCode, setQrCode] = useState(null);
  const [newSessionName, setNewSessionName] = useState("");

  // Carregar config salva do banco
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const { data } = await api.get("/companySettings");
      const settings = data || {};
      const url = settings.wacallsUrl || "";
      const savedEmail = settings.wacallsEmail || "";
      const savedPassword = settings.wacallsPassword || "";
      const savedToken = settings.wacallsToken || "";
      const savedSession = settings.wacallsSessionId || "";

      if (url) setWacallsUrl(url);
      if (savedEmail) setEmail(savedEmail);
      setSelectedSession(savedSession);

      if (url && savedEmail && savedPassword) {
        // Sempre fazer login novo para garantir token válido
        try {
          const res = await fetch(`${url}/api/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: savedEmail, password: savedPassword }),
          });
          if (res.ok) {
            const loginData = await res.json();
            const freshToken = loginData.token;
            setToken(freshToken);
            setLoggedIn(true);
            await saveConfigToDb("wacallsToken", freshToken);
            localStorage.setItem("wacalls_config", JSON.stringify({
              token: freshToken,
              sessionId: savedSession,
              url: url,
            }));
          } else {
            // Login falhou, tentar com token antigo
            if (savedToken) {
              setToken(savedToken);
              setLoggedIn(true);
              localStorage.setItem("wacalls_config", JSON.stringify({
                token: savedToken,
                sessionId: savedSession,
                url: url,
              }));
            }
          }
        } catch (e) {
          // Erro de rede, usar token salvo
          if (savedToken) {
            setToken(savedToken);
            setLoggedIn(true);
            localStorage.setItem("wacalls_config", JSON.stringify({
              token: savedToken,
              sessionId: savedSession,
              url: url,
            }));
          }
        }
      } else if (savedToken && url) {
        setToken(savedToken);
        setLoggedIn(true);
        localStorage.setItem("wacalls_config", JSON.stringify({
          token: savedToken,
          sessionId: savedSession,
          url: url,
        }));
      }
    } catch (e) {
      console.error("[WaCalls] Erro ao carregar config:", e);
    } finally {
      setLoadingConfig(false);
    }
  };

  // Salvar config no banco
  const saveConfigToDb = async (column, data) => {
    try {
      await api.put("/companySettings", { column, data });
    } catch (e) {
      console.error("[WaCalls] Erro ao salvar config:", e);
    }
  };

  // Renovar token automaticamente usando email/senha salvos
  const renewToken = async () => {
    try {
      const { data: settings } = await api.get("/companySettings");
      const savedUrl = settings?.wacallsUrl || wacallsUrl;
      const savedEmail = settings?.wacallsEmail || email;
      const savedPassword = settings?.wacallsPassword || "";

      if (!savedUrl || !savedEmail || !savedPassword) {
        console.warn("[WaCalls] Não é possível renovar token — credenciais incompletas");
        return null;
      }

      const res = await fetch(`${savedUrl}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: savedEmail, password: savedPassword }),
      });

      if (!res.ok) {
        console.error("[WaCalls] Falha ao renovar token:", res.status);
        return null;
      }

      const data = await res.json();
      const newToken = data.token;

      // Atualizar no banco, localStorage e estado
      await saveConfigToDb("wacallsToken", newToken);
      setToken(newToken);

      localStorage.setItem("wacalls_config", JSON.stringify({
        token: newToken,
        sessionId: selectedSession,
        url: savedUrl,
      }));

      console.log("[WaCalls] Token renovado com sucesso");
      return newToken;
    } catch (e) {
      console.error("[WaCalls] Erro ao renovar token:", e);
      return null;
    }
  };

  // Fetch com renovação automática de token
  const wacallsFetch = async (method, path, body) => {
    const doFetch = async (tkn) => {
      const opts = {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tkn}`,
        },
      };
      if (body) opts.body = JSON.stringify(body);
      return fetch(`${wacallsUrl}${path}`, opts);
    };

    let res = await doFetch(token);

    // Se 401 ou 403, tentar renovar token e refazer
    if (res.status === 401 || res.status === 403) {
      console.log("[WaCalls] Token expirado, renovando...");
      const newToken = await renewToken();
      if (newToken) {
        res = await doFetch(newToken);
      }
    }

    return res;
  };

  // Quando logado, carregar sessões e SSE
  useEffect(() => {
    if (loggedIn && token && wacallsUrl) {
      loadSessions();
      const cleanup = connectSSE();
      return cleanup;
    }
  }, [loggedIn, token, wacallsUrl]);

  const connectSSE = () => {
    if (!wacallsUrl || !token) return () => {};
    const clientId = `wacalls-page-${Date.now()}`;
    const es = new EventSource(`${wacallsUrl}/api/events?token=${token}&clientId=${clientId}`);
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "session-qr") {
          setQrCode(data.qr);
        }
        if (data.type === "auth-state" && data.paired) {
          setQrCode(null);
          loadSessions();
        }
        if (data.type === "session-list") {
          setSessions(data.sessions || []);
        }
      } catch (e) {}
    };
    es.onerror = () => {
      console.warn("[WaCalls] SSE erro de conexão");
    };
    return () => es.close();
  };

  const loadSessions = async () => {
    try {
      const res = await wacallsFetch("GET", "/api/sessions");
      if (!res.ok) {
        console.error("[WaCalls] Erro ao carregar sessões:", res.status);
        if (res.status === 401 || res.status === 403) {
          toast.error("Não foi possível autenticar no VoIP. Reconecte.");
          setLoggedIn(false);
          setToken("");
          localStorage.removeItem("wacalls_config");
        }
        return;
      }
      const data = await res.json();
      setSessions(data?.sessions || []);
    } catch (e) {
      console.error("[WaCalls] Erro ao carregar sessões:", e);
    }
  };

  // Login
  const handleLogin = async () => {
    if (!wacallsUrl || !email || !password) {
      toast.error("Preencha domínio, email e senha");
      return;
    }

    // Garantir que URL não termine com /
    const cleanUrl = wacallsUrl.replace(/\/+$/, "");

    setLoading(true);
    try {
      const res = await fetch(`${cleanUrl}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) throw new Error("Credenciais inválidas");
      const data = await res.json();

      // Salvar no banco
      await saveConfigToDb("wacallsUrl", cleanUrl);
      await saveConfigToDb("wacallsEmail", email);
      await saveConfigToDb("wacallsPassword", password);
      await saveConfigToDb("wacallsToken", data.token);

      // Salvar no localStorage para o widget global
      localStorage.setItem("wacalls_config", JSON.stringify({
        token: data.token,
        sessionId: "",
        url: cleanUrl,
      }));

      // Atualizar estado e ativar
      setWacallsUrl(cleanUrl);
      setToken(data.token);
      setLoggedIn(true);

      toast.success("Conectado ao VoIP!");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const handleLogout = async () => {
    await saveConfigToDb("wacallsToken", "");
    await saveConfigToDb("wacallsSessionId", "");
    localStorage.removeItem("wacalls_config");
    setToken("");
    setLoggedIn(false);
    setSessions([]);
    setSelectedSession("");
    toast.info("Desconectado do VoIP");
  };

  // Selecionar sessão
  const handleSelectSession = async (sid) => {
    setSelectedSession(sid);
    await saveConfigToDb("wacallsSessionId", sid);

    // Atualizar localStorage para o widget global
    localStorage.setItem("wacalls_config", JSON.stringify({
      token,
      sessionId: sid,
      url: wacallsUrl,
    }));

    toast.success("Sessão selecionada! O discador está pronto.");
  };

  // Criar sessão
  const handleCreateSession = async () => {
    const name = newSessionName.trim() || "Nova Sessão";

    if (createMode === "creds") {
      // Modo importação de credenciais
      await handleImportCreds(name);
      return;
    }

    // Modo QR Code (padrão)
    try {
      const res = await wacallsFetch("POST", "/api/sessions", { name });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = err.error || `Erro ${res.status}`;
        if (msg.includes("session limit")) {
          throw new Error("Limite de sessões atingido. Verifique o limite do seu plano.");
        }
        throw new Error(msg);
      }
      toast.success("Sessão criada! Escaneie o QR Code.");
      setNewSessionName("");
      setShowCreateModal(false);
      loadSessions();
    } catch (e) {
      toast.error("Erro ao criar sessão: " + e.message);
    }
  };

  // Importar credenciais da extensão CONECT 2
  const handleImportCreds = async (name) => {
    if (!credsJson.trim()) {
      toast.error("Cole o JSON das credenciais extraídas");
      return;
    }

    let creds;
    try {
      creds = JSON.parse(credsJson.trim());
    } catch (e) {
      toast.error("JSON inválido. Verifique o formato das credenciais.");
      return;
    }

    // Validar campos obrigatórios
    if (!creds.noiseKey || !creds.signedIdentityKey || !creds.signedPreKey || !creds.registrationId || !creds.me?.id || !creds.account) {
      toast.error("Credenciais incompletas. Verifique os campos obrigatórios.");
      return;
    }

    setImportingCreds(true);
    try {
      // 1. Criar sessão primeiro
      const createRes = await wacallsFetch("POST", "/api/sessions", { name });
      if (!createRes.ok) {
        const err = await createRes.json().catch(() => ({}));
        const msg = err.error || `Erro ${createRes.status}`;
        if (msg.includes("session limit")) {
          throw new Error("Limite de sessões atingido. Verifique o limite do seu plano.");
        }
        throw new Error(msg);
      }
      const sessionData = await createRes.json();
      const sid = sessionData.id;

      // 2. Importar credenciais na sessão criada
      const importRes = await wacallsFetch("POST", `/api/sessions/${sid}/import-creds`, creds);
      if (!importRes.ok) {
        const err = await importRes.json().catch(() => ({}));
        const msg = err.error || `Erro ${importRes.status}`;
        throw new Error(msg);
      }

      const result = await importRes.json();
      toast.success(`Sessão conectada via credenciais! JID: ${result.jid || "OK"}`);
      setNewSessionName("");
      setCredsJson("");
      setShowCreateModal(false);
      setCreateMode("qr");
      loadSessions();
    } catch (e) {
      toast.error("Erro ao importar credenciais: " + e.message);
    } finally {
      setImportingCreds(false);
    }
  };

  // Excluir sessão
  const handleDeleteSession = async (sid) => {
    if (!window.confirm("Excluir esta sessão?")) return;
    try {
      const res = await wacallsFetch("DELETE", `/api/sessions/${sid}`);
      if (!res.ok && res.status !== 204) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Erro ${res.status}`);
      }
      toast.success("Sessão excluída");
      if (selectedSession === sid) {
        setSelectedSession("");
        await saveConfigToDb("wacallsSessionId", "");
      }
      loadSessions();
    } catch (e) {
      toast.error("Erro: " + e.message);
    }
  };

  const getStatusColor = (state) => {
    switch (state) {
      case "open": return "#22c55e";
      case "qr": return "#f59e0b";
      case "connecting": return "#3b82f6";
      default: return "#ef4444";
    }
  };

  const getStatusLabel = (state) => {
    switch (state) {
      case "open": return "Conectado";
      case "qr": return "Aguardando QR";
      case "connecting": return "Conectando...";
      default: return "Desconectado";
    }
  };

  // Histórico de chamadas
  const [callHistory, setCallHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [recordings, setRecordings] = useState([]);

  // Modal criar sessão
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createMode, setCreateMode] = useState("qr"); // "qr" ou "creds"
  const [credsJson, setCredsJson] = useState("");
  const [importingCreds, setImportingCreds] = useState(false);

  const loadHistory = async () => {
    // Usar sessão selecionada ou a primeira disponível
    const sid = selectedSession || (sessions.length > 0 ? sessions[0].id : null);
    if (!sid) return;
    setLoadingHistory(true);
    try {
      const res = await wacallsFetch("GET", `/api/sessions/${sid}/history`);
      if (res.ok) {
        const data = await res.json();
        setCallHistory(data?.rows || []);
      }
    } catch (e) {
      console.error("[VoIP] Erro ao carregar histórico:", e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadRecordings = async () => {
    try {
      const res = await wacallsFetch("GET", "/api/recordings");
      if (res.ok) {
        const data = await res.json();
        setRecordings(data?.recordings || []);
      }
    } catch (e) {
      console.error("[VoIP] Erro ao carregar gravações:", e);
    }
  };

  const handleDownloadRecording = (rid) => {
    const url = `${wacallsUrl}/api/recordings/${rid}/download?token=${token}`;
    window.open(url, "_blank");
  };

  const [playingAudio, setPlayingAudio] = useState(null);
  const [playingId, setPlayingId] = useState(null);

  const handlePlayRecording = (rid) => {
    // Se já está tocando este, pausar
    if (playingId === rid && playingAudio) {
      playingAudio.pause();
      setPlayingAudio(null);
      setPlayingId(null);
      return;
    }
    // Parar áudio anterior
    if (playingAudio) {
      playingAudio.pause();
    }
    const audioUrl = `${wacallsUrl}/api/recordings/${rid}/download?token=${token}`;
    const audio = new Audio(audioUrl);
    audio.play();
    audio.onended = () => {
      setPlayingAudio(null);
      setPlayingId(null);
    };
    setPlayingAudio(audio);
    setPlayingId(rid);
  };

  const handleDeleteRecording = async (rid) => {
    if (!window.confirm("Excluir esta gravação?")) return;
    try {
      const res = await wacallsFetch("DELETE", `/api/recordings/${rid}`);
      if (res.ok || res.status === 204) {
        toast.success("Gravação excluída");
        loadRecordings();
      }
    } catch (e) {
      toast.error("Erro ao excluir gravação");
    }
  };

  // Carregar histórico e gravações quando tiver sessões
  useEffect(() => {
    if (loggedIn && sessions.length > 0) {
      loadHistory();
      loadRecordings();
    }
  }, [selectedSession, loggedIn, sessions]);

  const formatPhone = (peer) => {
    if (!peer) return "Desconhecido";
    return peer.replace("@s.whatsapp.net", "");
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return "-";
    const ms = end - start;
    const sec = Math.floor(ms / 1000);
    const min = Math.floor(sec / 60);
    const s = sec % 60;
    return `${min}:${s.toString().padStart(2, "0")}`;
  };

  const formatDate = (ts) => {
    if (!ts) return "";
    return new Date(ts).toLocaleString("pt-BR");
  };

  if (loadingConfig) {
    return (
      <MainContainer>
        <div className={classes.container}>
          <p>Carregando configurações...</p>
        </div>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <div className={classes.container}>
        <div className={classes.header}>
          <h1 className={classes.title}>📞 VoIP - Chamadas WhatsApp</h1>
          <p className={classes.subtitle}>
            {loggedIn
              ? "Configure suas sessões e use o discador flutuante para ligar"
              : "Conecte sua conta VoIP para fazer chamadas"}
          </p>
        </div>

        {!loggedIn ? (
          /* Tela de Login */
          <div className={classes.panel} style={{ maxWidth: "420px" }}>
            <div className={classes.panelHeader}>
              <h3>🔐 Conectar ao VoIP</h3>
            </div>
            <div className={classes.panelBody}>
              <div className={classes.formGroup}>
                <label className={classes.label}>Domínio / URL do servidor</label>
                <input
                  type="text"
                  className={classes.input}
                  value={wacallsUrl}
                  onChange={(e) => setWacallsUrl(e.target.value)}
                  placeholder="https://voip.seudominio.com.br"
                />
                <p style={{ fontSize: "11px", color: "#666", marginTop: "4px" }}>
                  Ex: https://voip.loginzap.com.br
                </p>
              </div>
              <div className={classes.formGroup}>
                <label className={classes.label}>Email</label>
                <input
                  type="email"
                  className={classes.input}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                />
              </div>
              <div className={classes.formGroup}>
                <label className={classes.label}>Senha</label>
                <input
                  type="password"
                  className={classes.input}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                />
              </div>
              <button
                className={`${classes.btn} ${classes.btnPrimary}`}
                onClick={handleLogin}
                disabled={loading}
                style={{ width: "100%", justifyContent: "center", padding: "12px" }}
              >
                <PhoneIcon style={{ fontSize: 18 }} />
                {loading ? "Conectando..." : "Conectar"}
              </button>
              <div style={{ marginTop: "12px", padding: "10px", background: "#f0f9ff", borderRadius: "6px", fontSize: "12px", color: "#0369a1" }}>
                💡 Informe o domínio do seu servidor VoIP e suas credenciais de acesso.
              </div>
            </div>
          </div>
        ) : (
          /* Tela Principal */
          <>
            <div style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div className={classes.statusCard} style={{ margin: 0, flex: 1 }}>
                ✅ Conectado ao VoIP ({wacallsUrl})
                {selectedSession && " • Sessão ativa — use o discador no canto inferior esquerdo"}
              </div>
              <button
                className={`${classes.btn} ${classes.btnDanger}`}
                onClick={handleLogout}
                style={{ marginLeft: "12px" }}
              >
                Desconectar
              </button>
            </div>

            <div className={classes.grid}>
              {/* Sessões */}
              <div className={classes.panel}>
                <div className={classes.panelHeader}>
                  <h3>📱 Sessões WhatsApp</h3>
                  <button
                    className={`${classes.btn} ${classes.btnOutline}`}
                    onClick={loadSessions}
                    style={{ padding: "6px 10px" }}
                  >
                    <RefreshIcon style={{ fontSize: 16 }} />
                  </button>
                </div>
                <div className={classes.panelBody}>
                  <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
                    <input
                      type="text"
                      className={classes.input}
                      value={newSessionName}
                      onChange={(e) => setNewSessionName(e.target.value)}
                      placeholder="Nome da sessão"
                      style={{ flex: 1 }}
                      onKeyDown={(e) => e.key === "Enter" && setShowCreateModal(true)}
                    />
                    <button
                      className={`${classes.btn} ${classes.btnPrimary}`}
                      onClick={() => setShowCreateModal(true)}
                    >
                      <AddIcon style={{ fontSize: 16 }} /> Criar
                    </button>
                  </div>

                  {sessions.length === 0 ? (
                    <p style={{ textAlign: "center", color: "#999", fontSize: "13px" }}>
                      Nenhuma sessão. Crie uma para começar.
                    </p>
                  ) : (
                    sessions.map((s) => (
                      <div key={s.id} className={classes.sessionItem}>
                        <div className={classes.sessionInfo}>
                          <span className={classes.sessionName}>
                            {selectedSession === s.id && "✓ "}
                            {s.name || s.id.slice(0, 10)}
                          </span>
                          <span className={classes.sessionStatus}>
                            <span className={classes.dot} style={{ background: getStatusColor(s.state) }} />
                            {getStatusLabel(s.state)}
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: "6px" }}>
                          {s.state === "open" && (
                            <button
                              className={`${classes.btn} ${
                                selectedSession === s.id ? classes.btnPrimary : classes.btnOutline
                              }`}
                              onClick={() => handleSelectSession(s.id)}
                              style={{ padding: "6px 10px", fontSize: "11px" }}
                            >
                              {selectedSession === s.id ? "Ativa" : "Usar"}
                            </button>
                          )}
                          <button
                            className={`${classes.btn} ${classes.btnOutline}`}
                            onClick={() => handleDeleteSession(s.id)}
                            style={{ padding: "6px 8px" }}
                          >
                            <DeleteIcon style={{ fontSize: 14, color: "#ef4444" }} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* QR Code + Instruções */}
              <div className={classes.panel}>
                <div className={classes.panelHeader}>
                  <h3>📋 Como usar</h3>
                </div>
                <div className={classes.panelBody}>
                  {qrCode && (
                    <div style={{ textAlign: "center", marginBottom: "16px" }}>
                      <p style={{ fontSize: "13px", color: "#374151", marginBottom: "8px", fontWeight: 600 }}>
                        📷 Escaneie o QR Code no WhatsApp:
                      </p>
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrCode)}`}
                        alt="QR Code"
                        className={classes.qrImg}
                        style={{ width: "180px", height: "180px" }}
                      />
                    </div>
                  )}

                  <ol style={{ margin: 0, paddingLeft: "20px", fontSize: "13px", color: "#374151", lineHeight: "2" }}>
                    <li><strong>Informe o domínio</strong> do seu servidor WaCalls e faça login</li>
                    <li><strong>Crie uma sessão</strong> e escaneie o QR Code no WhatsApp</li>
                    <li><strong>Selecione a sessão</strong> clicando em "Usar"</li>
                    <li><strong>Use o discador</strong> no canto inferior esquerdo da tela</li>
                    <li>Digite o número com DDI (ex: 5511999999999) e clique em 📞</li>
                  </ol>

                  <div style={{ marginTop: "16px", padding: "12px", background: "#fef3c7", borderRadius: "8px", fontSize: "12px", color: "#92400e" }}>
                    ⚠️ O discador aparece em todas as páginas quando uma sessão está ativa.
                  </div>
                </div>
              </div>
            </div>

            {/* Histórico de Chamadas */}
            {sessions.length > 0 && (
              <div className={classes.panel} style={{ marginTop: "24px" }}>
                <div className={classes.panelHeader}>
                  <h3>📜 Histórico de Chamadas</h3>
                  <button
                    className={`${classes.btn} ${classes.btnOutline}`}
                    onClick={loadHistory}
                    disabled={loadingHistory}
                    style={{ padding: "6px 10px" }}
                  >
                    <RefreshIcon style={{ fontSize: 16 }} />
                    {loadingHistory ? " Carregando..." : ""}
                  </button>
                </div>
                <div style={{ overflowX: "auto" }}>
                  {callHistory.length === 0 ? (
                    <div style={{ padding: "24px", textAlign: "center", color: "#999", fontSize: "13px" }}>
                      {loadingHistory ? "Carregando..." : "Nenhuma chamada registrada nesta sessão"}
                    </div>
                  ) : (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                      <thead>
                        <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e8ecf1" }}>
                          <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, color: "#374151" }}>Direção</th>
                          <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, color: "#374151" }}>Número</th>
                          <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, color: "#374151" }}>Data/Hora</th>
                          <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, color: "#374151" }}>Duração</th>
                          <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, color: "#374151" }}>Status</th>
                          <th style={{ padding: "12px 16px", textAlign: "center", fontWeight: 600, color: "#374151" }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {callHistory.map((call, idx) => {
                          // Buscar gravação correspondente a esta chamada
                          const rec = recordings.find(r => r.call_id === call.callId);
                          return (
                          <tr key={idx} style={{ borderBottom: "1px solid #f5f5f5" }}>
                            <td style={{ padding: "10px 16px" }}>
                              <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "2px 8px",
                                borderRadius: "12px",
                                fontSize: "11px",
                                fontWeight: 600,
                                background: call.direction === "outbound" ? "#dbeafe" : "#dcfce7",
                                color: call.direction === "outbound" ? "#1d4ed8" : "#166534",
                              }}>
                                {call.direction === "outbound" ? "↗ Saída" : "↙ Entrada"}
                              </span>
                            </td>
                            <td style={{ padding: "10px 16px", fontWeight: 600, fontFamily: "monospace" }}>
                              {formatPhone(call.peer)}
                            </td>
                            <td style={{ padding: "10px 16px", color: "#666" }}>
                              {formatDate(call.startedAt)}
                            </td>
                            <td style={{ padding: "10px 16px", fontFamily: "monospace" }}>
                              {formatDuration(call.startedAt, call.endedAt)}
                            </td>
                            <td style={{ padding: "10px 16px" }}>
                              <span style={{
                                padding: "2px 8px",
                                borderRadius: "12px",
                                fontSize: "11px",
                                fontWeight: 600,
                                background: call.status === "ended" ? "#f3f4f6" : "#fef3c7",
                                color: call.status === "ended" ? "#374151" : "#92400e",
                              }}>
                                {call.endReason || call.status || "-"}
                              </span>
                            </td>
                            <td style={{ padding: "10px 16px", textAlign: "center" }}>
                              {rec ? (
                                <div style={{ display: "flex", gap: "8px", justifyContent: "center", alignItems: "center" }}>
                                  <button
                                    onClick={() => handlePlayRecording(rec.id)}
                                    title={playingId === rec.id ? "Pausar" : "Reproduzir"}
                                    style={{
                                      width: "30px", height: "30px", borderRadius: "50%", border: "none",
                                      background: "transparent", cursor: "pointer", display: "flex", alignItems: "center",
                                      justifyContent: "center", color: "#374151",
                                    }}
                                  >
                                    {playingId === rec.id ? <PauseIcon style={{ fontSize: 20 }} /> : <PlayArrowIcon style={{ fontSize: 20 }} />}
                                  </button>
                                  <button
                                    onClick={() => handleDownloadRecording(rec.id)}
                                    title="Baixar gravação"
                                    style={{
                                      width: "30px", height: "30px", borderRadius: "50%", border: "none",
                                      background: "transparent", cursor: "pointer", display: "flex", alignItems: "center",
                                      justifyContent: "center", color: "#374151",
                                    }}
                                  >
                                    <DownloadIcon style={{ fontSize: 20 }} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteRecording(rec.id)}
                                    title="Excluir gravação"
                                    style={{
                                      width: "30px", height: "30px", borderRadius: "50%", border: "none",
                                      background: "transparent", cursor: "pointer", display: "flex", alignItems: "center",
                                      justifyContent: "center", color: "#ef4444",
                                    }}
                                  >
                                    <DeleteOutlineIcon style={{ fontSize: 20 }} />
                                  </button>
                                </div>
                              ) : (
                                <span style={{ color: "#ccc", fontSize: "11px" }}>—</span>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Modal Criar Sessão */}
        {showCreateModal && (
          <div className={classes.modalOverlay} onClick={() => setShowCreateModal(false)}>
            <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={classes.modalHeader}>
                <h3>➕ Criar Sessão</h3>
                <button
                  className={classes.modalCloseBtn}
                  onClick={() => { setShowCreateModal(false); setCreateMode("qr"); setCredsJson(""); }}
                >
                  <CloseIcon style={{ fontSize: 20 }} />
                </button>
              </div>
              <div className={classes.modalBody}>
                {/* Nome da sessão */}
                <div className={classes.formGroup}>
                  <label className={classes.label}>Nome da sessão</label>
                  <input
                    type="text"
                    className={classes.input}
                    value={newSessionName}
                    onChange={(e) => setNewSessionName(e.target.value)}
                    placeholder="Ex: WhatsApp Comercial"
                  />
                </div>

                {/* Escolher modo */}
                <label className={classes.label} style={{ marginBottom: "12px" }}>Como deseja conectar?</label>

                <div
                  className={`${classes.optionCard} ${createMode === "qr" ? classes.optionCardActive : ""}`}
                  onClick={() => setCreateMode("qr")}
                >
                  <div className={classes.optionTitle}>
                    <QrCodeIcon style={{ fontSize: 20, color: "#3b82f6" }} />
                    QR Code
                  </div>
                  <p className={classes.optionDesc}>
                    Escaneie o QR Code com o WhatsApp para parear (método tradicional)
                  </p>
                </div>

                <div
                  className={`${classes.optionCard} ${createMode === "creds" ? classes.optionCardActive : ""}`}
                  onClick={() => setCreateMode("creds")}
                >
                  <div className={classes.optionTitle}>
                    <VpnKeyIcon style={{ fontSize: 20, color: "#16a34a" }} />
                    Importar Credenciais (CONECT 2)
                  </div>
                  <p className={classes.optionDesc}>
                    Cole as credenciais extraídas pela extensão CONECT 2 — conecta sem QR Code
                  </p>
                </div>

                {/* Textarea para creds (só aparece no modo creds) */}
                {createMode === "creds" && (
                  <div style={{ marginTop: "16px" }}>
                    <label className={classes.label}>JSON das Credenciais</label>
                    <textarea
                      className={classes.textarea}
                      value={credsJson}
                      onChange={(e) => setCredsJson(e.target.value)}
                      placeholder={`Cole aqui o JSON extraído pela extensão CONECT 2.\nEx:\n{\n  "noiseKey": { "private": "...", "public": "..." },\n  "signedIdentityKey": { "private": "...", "public": "..." },\n  "signedPreKey": { "keyId": 1, "keyPair": {...}, "signature": "..." },\n  "registrationId": 6438,\n  "me": { "id": "5524...@s.whatsapp.net" },\n  "account": { "details": "...", ... }\n}`}
                    />
                    <p style={{ fontSize: "11px", color: "#666", marginTop: "6px" }}>
                      💡 Extraia as credenciais usando a extensão CONECT 2 no Chrome com o WhatsApp Web logado.
                    </p>
                  </div>
                )}

                {/* Botão confirmar */}
                <div style={{ marginTop: "20px", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                  <button
                    className={`${classes.btn} ${classes.btnOutline}`}
                    onClick={() => { setShowCreateModal(false); setCreateMode("qr"); setCredsJson(""); }}
                  >
                    Cancelar
                  </button>
                  <button
                    className={`${classes.btn} ${classes.btnPrimary}`}
                    onClick={handleCreateSession}
                    disabled={importingCreds || (createMode === "creds" && !credsJson.trim())}
                    style={{ padding: "10px 24px" }}
                  >
                    {importingCreds
                      ? "Importando..."
                      : createMode === "creds"
                        ? "🔑 Importar e Conectar"
                        : "📷 Criar com QR Code"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainContainer>
  );
};

export default WaCalls;
